import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import { Textarea } from "@/shared/components/ui/textarea";
import { Input } from "@/shared/components/ui/input";
import { LengthNumberInput } from "@/shared/components/domain/qpm/inputs/length-number-input";
import {
  WeightNumberInput,
  WeightUnit,
} from "@/shared/components/domain/qpm/inputs/weight-number-input";
import {
  AreaNumberInput,
  AreaUnit,
} from "@/shared/components/domain/qpm/inputs/area-number-input";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorDetailsView } from "@/services/backend/qpm/monitors/types/monitor-view";
import { DetailsDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";

export type EditDetailsProps = {
  details: MonitorDetailsView | DetailsDTO;
  onUpdate: (details: Partial<MonitorDetailsView | DetailsDTO>) => void;
  type: MonitorType;
  readOnly?: boolean;
};

export function EditDetailsComponent({
  details,
  onUpdate,
  type,
  readOnly,
}: EditDetailsProps) {
  const disabled = readOnly;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="detailsDescriptionText">{t("Beschreibung")}</Label>
        <Textarea
          id="detailsDescriptionText"
          value={details.description}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) return;
            onUpdate({ description: e.target.value });
          }}
        />
      </div>
      {type === MonitorType.FlyExterminator && (
        <>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="installationTypeText">{t("Montageart")}</Label>
            <Input
              id="installationTypeText"
              value={details.installationType}
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                onUpdate({ installationType: e.target.value });
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="detailsHeightValue">{t("Höhe")}</Label>
              <LengthNumberInput
                id="detailsHeightValue"
                value={details.heightMM}
                disabled={disabled}
                onChange={(val) => {
                  if (disabled) return;
                  onUpdate({ heightMM: val });
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="detailsWidthValue">{t("Breite")}</Label>
              <LengthNumberInput
                id="detailsWidthValue"
                value={details.widthMM}
                disabled={disabled}
                onChange={(val) => {
                  if (disabled) return;
                  onUpdate({ widthMM: val });
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="detailsDepthValue">{t("Tiefe")}</Label>
              <LengthNumberInput
                id="detailsDepthValue"
                value={details.depthMM}
                disabled={disabled}
                onChange={(val) => {
                  if (disabled) return;
                  onUpdate({ depthMM: val });
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="detailsWeightValue">{t("Gewicht")}</Label>
              <WeightNumberInput
                id="detailsWeightValue"
                value={details.weightG}
                defaultUnit={WeightUnit.Kilograms}
                disabled={disabled}
                onChange={(val) => {
                  if (disabled) return;
                  onUpdate({ weightG: val });
                }}
              />
            </div>
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="coverageText">{t("Wirkungsbereich")}</Label>
            <div className="flex items-center justify-start gap-2">
              <Input
                id="coverageText"
                value={details.coveragePreText}
                disabled={disabled}
                onChange={(e) => {
                  if (disabled) return;
                  onUpdate({ coveragePreText: e.target.value });
                }}
              />
              <AreaNumberInput
                value={details.coverageValue}
                defaultUnit={AreaUnit.SquareMeters}
                disabled={disabled}
                onChange={(val) => {
                  if (disabled) return;
                  onUpdate({ coverageValue: val });
                }}
              />
              <Input
                id="coverageText"
                value={details.coveragePostText}
                disabled={disabled}
                onChange={(e) => {
                  if (disabled) return;
                  onUpdate({ coveragePostText: e.target.value });
                }}
              />
            </div>
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="starterText">{t("Starter")}</Label>
            <Input
              id="starterText"
              value={details.starterSpecification}
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                onUpdate({ starterSpecification: e.target.value });
              }}
            />
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="protectionLevelText">{t("Schutzstufe")}</Label>
            <Input
              id="protectionLevelText"
              value={details.protectionLevel}
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                onUpdate({ protectionLevel: e.target.value });
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
